import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { S25IconComponent } from "../../../../../modules/s25-icon/s25.icon.component";
import { S25ItemComponent } from "../../../../../modules/s25-item/s25.item.component";
import { FavoriteService } from "../../../../../services/favorite.service";
import { StateService } from "../../../../../services/state.service";
import { ModalService } from "../../../../../modules/modal/modal.service";
import { S25Util } from "../../../../../util/s25-util";
import {
    dashDitemRowIconType,
    dashListItemIsFavorited,
    dashListItemObjectTypeId,
    dashListItemShowsEdit,
    dashListItemShowsFavorite,
    dashListItemShowsTypeIcon,
    DashDitemTypeId,
} from "./home.dash.panel.list.item.util";
import { DashPanelListItemBean } from "./home.dash.panel.list.item.types";
import {
    type DashDitemDisplayLang,
    formatDashListItemAriaLabel,
    formatDashListItemCoreDesc,
    formatDashListItemCoreLabel,
    formatDashListItemCoreLabelItalic,
    formatDashListItemCoreName,
} from "./home.dash.panel.list.ditem.format";

@TypeManagerDecorator("s25-ng-dash-panel-list-item")
@Component({
    selector: "s25-ng-dash-panel-list-item",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: "s25-ditem" },
    imports: [NgTemplateOutlet, S25IconComponent, S25ItemComponent],
    template: `
        <ng-template #itemCoreText>
            <div class="item-name">
                <span class="item-text">{{ displayName() }}</span>
            </div>
            @if (displayLabel()) {
                <span class="item-label">&nbsp;{{ displayLabel() }}</span>
            }
            @if (displayLabelItalic()) {
                <span class="item-label label-italic">&nbsp;{{ displayLabelItalic() }}</span>
            }
            @if (displayDesc()) {
                <div class="item-desc">
                    <span class="item-text">{{ displayDesc() }}</span>
                </div>
            }
        </ng-template>
        <div class="item-main handler-col" [class.inactive]="item().isInactive">
            <div class="left-icon-row leftColWrapper" [class.no-icon]="!showTypeIcon()">
                @if (showTypeIcon() && rowIconType(); as iconType) {
                    <div class="icon-col" aria-hidden="true">
                        <s25-ng-icon [type]="iconType" [ariaLabel]="false" />
                    </div>
                }
                <div class="text-col">
                    @if (objectTypeId(); as objectTypeId) {
                        <s25-item
                            [itemId]="item().itemId!"
                            [itemTypeId]="objectTypeId"
                            [itemName]="displayName()"
                            [hideLabel]="true"
                            [inactive]="!!item().isInactive"
                            [ariaLabel]="ariaLabel()"
                            [hidePopover]="blockS25ItemChrome()"
                            [blockContextMenu]="blockS25ItemChrome()"
                            [detailsOverrideFn]="navigateItem"
                            [contextMenuBean]="item()"
                        >
                            <ng-container [ngTemplateOutlet]="itemCoreText" />
                        </s25-item>
                    } @else {
                        <div
                            class="handler-bound"
                            tabindex="0"
                            role="link"
                            [attr.aria-label]="ariaLabel()"
                            (click)="navigateItem($event)"
                            (keyup.enter)="navigateItem($event)"
                        >
                            <ng-container [ngTemplateOutlet]="itemCoreText" />
                        </div>
                    }
                </div>
            </div>
            @if (showEdit()) {
                <div
                    class="edit-col handler-bound"
                    role="button"
                    tabindex="0"
                    [attr.aria-label]="editAriaLabel()"
                    (click)="openInRose($event)"
                    (keyup.enter)="openInRose($event)"
                    (keydown.space)="openInRose($event)"
                >
                    <s25-ng-icon [type]="editIconType()" [ariaLabel]="false" />
                </div>
            }
            @if (showFavorite()) {
                <div
                    class="favorite-col handler-bound"
                    role="button"
                    tabindex="0"
                    [attr.aria-label]="favoriteAriaLabel()"
                    (click)="toggleFavorite($event)"
                    (keyup.enter)="toggleFavorite($event)"
                    (keydown.space)="toggleFavorite($event)"
                >
                    <s25-ng-icon
                        [type]="isFavorited() ? 'starOn' : 'starOff'"
                        [ariaLabel]="false"
                        [class.c-starBtn]="isFavorited()"
                    />
                </div>
            }
        </div>
    `,
})
export class HomeDashPanelListItemComponent {
    readonly item = input.required<DashPanelListItemBean>();
    readonly loggedIn = input(false);
    readonly ditemLang = input.required<DashDitemDisplayLang>();

    private readonly favorited = signal<boolean | undefined>(undefined);

    readonly ditemTypeId = computed(() => this.item().itemTypeId);
    readonly objectTypeId = computed(() => dashListItemObjectTypeId(this.ditemTypeId(), this.item().itemId));
    readonly blockS25ItemChrome = computed(() => !!this.item().isInactive || !!this.item().isPrivate);
    readonly showTypeIcon = computed(() => dashListItemShowsTypeIcon(this.ditemTypeId()));
    readonly showEdit = computed(() => this.loggedIn() && dashListItemShowsEdit(this.ditemTypeId(), this.item()));
    readonly showFavorite = computed(() => this.loggedIn() && dashListItemShowsFavorite(this.ditemTypeId()));
    readonly isFavorited = computed(() => this.favorited() ?? dashListItemIsFavorited(this.item()));

    readonly displayName = computed(() => formatDashListItemCoreName(this.item(), this.ditemLang()));
    readonly displayLabel = computed(() => formatDashListItemCoreLabel(this.item(), this.ditemLang()));
    readonly displayLabelItalic = computed(() => formatDashListItemCoreLabelItalic(this.item(), this.ditemLang()));
    readonly displayDesc = computed(() => formatDashListItemCoreDesc(this.item()));
    readonly ariaLabel = computed(() => formatDashListItemAriaLabel(this.item(), this.ditemLang()));

    readonly rowIconType = computed(() => dashDitemRowIconType(this.ditemTypeId()));

    readonly editIconType = computed(() => "scheduling" as const);

    readonly editAriaLabel = computed(() =>
        this.ditemTypeId() === DashDitemTypeId.FavoriteEvent ? "Open in Event Form" : "Create New Event",
    );

    readonly favoriteAriaLabel = computed(() => (this.isFavorited() ? "Remove from Favorites" : "Add to Favorites"));

    readonly navigateItem = (event: KeyboardEvent | MouseEvent) => this.navigate(event);

    navigate(event: KeyboardEvent | MouseEvent): Promise<unknown> {
        const row = this.item();
        if (row.isInactive || row.isPrivate || row.itemTypeId === 0) {
            return Promise.resolve();
        }
        event.stopPropagation();
        event.preventDefault();

        if (row.itemTypeId === DashDitemTypeId.TaskRow) {
            return ModalService.modal("create-edit-task", {
                type: "edit",
                taskType: row.taskType!,
                taskId: S25Util.parseInt(row.itemId),
                eventId: row.eventId,
                taskActionsCell: row as never,
                listModelBean: row.listModelBean,
            });
        }

        const newTab =
            event.ctrlKey ||
            event.metaKey ||
            (event instanceof MouseEvent && (event.button === 1 || event.which === 2));
        return StateService.gotoItem(row as never, newTab);
    }

    /**
     * Create New Event / Open in Event Form.
     * ANG-7662: always pass this row's item — legacy s25-ditem hard-coded index `0` on keypress.
     */
    openInRose(event: Event): void {
        event.stopPropagation();
        if (event instanceof KeyboardEvent && event.key === " ") {
            event.preventDefault();
        }
        void StateService.gotoRose(this.item());
    }

    toggleFavorite(event: Event): void {
        event.stopPropagation();
        if (event instanceof KeyboardEvent && event.key === " ") {
            event.preventDefault();
        }
        const row = this.item();
        const currentlyFavorited = dashListItemIsFavorited(row);
        const nextFavorited = !currentlyFavorited;
        this.favorited.set(nextFavorited);
        row.isUnstar = !nextFavorited;
        void FavoriteService.toggle({
            itemId: row.itemId!,
            itemTypeId: row.itemTypeId!,
            isFav: currentlyFavorited,
        }).catch(() => {
            this.favorited.set(currentlyFavorited);
            row.isUnstar = !currentlyFavorited;
        });
    }
}
